import React, { createContext, ReactElement, useContext, useReducer } from 'react';
import { WidgetType } from 'domain/entity/widget';

export enum ACTION_TYPE {
    SET_CURRENT_WIDGET_CONFIGURATION = 'SET_CURRENT_WIDGET_CONFIGURATION',
    SET_AVAILABLE_WIDGET_TYPES = 'SET_AVAILABLE_WIDGET_TYPES',
    RESET = 'RESET',
    SET_WIDGET_TYPE = 'SET_WIDGET_TYPE',
    SET_PRESET = 'SET_PRESET',
    SET_FEATURE = 'SET_FEATURE',
}

export type NONE = 'NONE';

export type KeyAndNamePair = {
    key: string | NONE;
    name: string | NONE;
};

export const initialState: CurrentWidgetConfiguration = {
    widgetType: WidgetType.NONE,
    feature: { key: 'NONE', name: 'NONE' },
    preset: { key: 'NONE', name: 'NONE' },
    availableWidgetTypes: [],
    widgetId: 'NONE',
};

type SetCurrentWidgetConfiguration = {
    type: ACTION_TYPE.SET_CURRENT_WIDGET_CONFIGURATION;
    payload: {
        widgetType: WidgetType;
        preset: KeyAndNamePair;
        feature: KeyAndNamePair;
        widgetId: string;
    };
};

type SetWidgetType = {
    type: ACTION_TYPE.SET_WIDGET_TYPE;
    payload: {
        widgetType: WidgetType;
    };
};

type SetPreset = {
    type: ACTION_TYPE.SET_PRESET;
    payload: KeyAndNamePair;
};

type SetFeature = {
    type: ACTION_TYPE.SET_FEATURE;
    payload: KeyAndNamePair;
};

type Reset = {
    type: ACTION_TYPE.RESET;
    payload: undefined;
};

type SetAvailableWidgetTypes = {
    type: ACTION_TYPE.SET_AVAILABLE_WIDGET_TYPES;
    payload: string[];
};

export type CurrentWidgetConfiguration = {
    widgetType: WidgetType;
    feature: KeyAndNamePair;
    preset: KeyAndNamePair;
    availableWidgetTypes: string[];
    widgetId: string | NONE;
};

export type CurrentWidgetConfigurationDispatch = {
    setCurrentWidgetConfiguration: (
        widgetType: WidgetType,
        feature: KeyAndNamePair,
        preset: KeyAndNamePair,
        widgetId: string | NONE
    ) => void;
    reset: () => void;
    setAvailableWidgetTypes: (widgetTypes: string[]) => void;
    setWidgetType: (widgetType: WidgetType) => void;
    setPreset: (presetKey: KeyAndNamePair) => void;
    setFeature: (featureKey: KeyAndNamePair) => void;
};

type CurrentWidgetConfigurationActionType =
    | SetCurrentWidgetConfiguration
    | Reset
    | SetAvailableWidgetTypes
    | SetWidgetType
    | SetFeature
    | SetPreset;

export const useCurrentWidgetConfigurationReducer = (
    state: CurrentWidgetConfiguration,
    action: CurrentWidgetConfigurationActionType
): CurrentWidgetConfiguration => {
    const { type, payload } = action;
    switch (type) {
        case ACTION_TYPE.SET_CURRENT_WIDGET_CONFIGURATION: {
            return {
                ...state,
                widgetType: payload.widgetType,
                feature: payload.feature,
                preset: payload.preset,
                widgetId: payload.widgetId,
            };
        }
        case ACTION_TYPE.SET_WIDGET_TYPE: {
            return {
                ...state,
                widgetType: payload.widgetType,
            };
        }
        case ACTION_TYPE.SET_FEATURE: {
            return {
                ...state,
                feature: payload,
            };
        }
        case ACTION_TYPE.SET_PRESET: {
            return {
                ...state,
                preset: payload,
            };
        }
        case ACTION_TYPE.SET_AVAILABLE_WIDGET_TYPES: {
            return {
                ...state,
                availableWidgetTypes: payload,
            };
        }
        case ACTION_TYPE.RESET:
        default: {
            return initialState;
        }
    }
};

type CurrentWidgetConfigurationProvider = {
    children: ReactElement;
    initialState?: CurrentWidgetConfiguration;
};

const CurrentWidgetConfigurationDispatchContext = createContext({} as CurrentWidgetConfigurationDispatch);
const CurrentWidgetConfigurationStateContext = createContext({} as CurrentWidgetConfiguration);

const dispatchCurrentWidgetConfiguration = (): CurrentWidgetConfigurationDispatch => useContext(CurrentWidgetConfigurationDispatchContext);
const useCurrentWidgetConfiguration = (): CurrentWidgetConfiguration => useContext(CurrentWidgetConfigurationStateContext);

const CurrentWidgetConfigurationProvider = (props: CurrentWidgetConfigurationProvider): ReactElement => {
    const { children, initialState: specificInitialState } = props;
    const [state, dispatch] = useReducer(useCurrentWidgetConfigurationReducer, specificInitialState ?? initialState);

    const setCurrentWidgetConfiguration = (
        widgetType: WidgetType,
        feature: KeyAndNamePair,
        preset: KeyAndNamePair,
        widgetId: string | NONE
    ) =>
        dispatch({
            type: ACTION_TYPE.SET_CURRENT_WIDGET_CONFIGURATION,
            payload: {
                widgetType,
                feature,
                preset,
                widgetId,
            },
        });
    const setPreset = (preset: KeyAndNamePair) => {
        dispatch({
            type: ACTION_TYPE.SET_PRESET,
            payload: preset,
        });
    };

    const setWidgetType = (widgetType: WidgetType) =>
        dispatch({
            type: ACTION_TYPE.SET_WIDGET_TYPE,
            payload: {
                widgetType,
            },
        });
    const setFeature = (feature: KeyAndNamePair) => {
        dispatch({
            type: ACTION_TYPE.SET_FEATURE,
            payload: feature,
        });
    };

    const reset = () =>
        dispatch({
            type: ACTION_TYPE.RESET,
            payload: undefined,
        });

    const setAvailableWidgetTypes = (widgetTypes: string[]) => {
        dispatch({
            type: ACTION_TYPE.SET_AVAILABLE_WIDGET_TYPES,
            payload: widgetTypes,
        });
    };

    return (
        <CurrentWidgetConfigurationStateContext.Provider value={state}>
            <CurrentWidgetConfigurationDispatchContext.Provider
                value={{
                    setCurrentWidgetConfiguration,
                    reset,
                    setAvailableWidgetTypes,
                    setPreset,
                    setFeature,
                    setWidgetType,
                }}
            >
                {children}
            </CurrentWidgetConfigurationDispatchContext.Provider>
        </CurrentWidgetConfigurationStateContext.Provider>
    );
};

export { CurrentWidgetConfigurationProvider, dispatchCurrentWidgetConfiguration, useCurrentWidgetConfiguration };
